/**
 * Roadmap Timeline Widget (issue #1818, successor to #1656, part of epic
 * #1650)
 *
 * Every `Roadmap`, each with its member `ChangeInitiative`s resolved via the
 * `PART_OF` reverse-edge, matching `homepage-dashboard-spec.md`'s "Roadmap
 * Timeline" widget. Calls the `roadmapTimeline` GraphQL query directly
 * (ADR-0003 GraphQL-for-reads — no REST route) built by the prerequisite
 * issue #1746 (`backend/src/graphql/resolvers/roadmaps.ts`). Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 *
 * Per issue #1761 (open, tracked separately, not a blocker here): production
 * `ChangeInitiative` rows have no `status` set yet — a roadmap's initiatives
 * still render (status is independent of the roadmap/initiative link), but
 * each initiative's status chip is simply omitted when absent, the same
 * fallback `ActiveInitiativesWidget` uses.
 */

import { useRoadmapTimeline, type RoadmapTimelineEntry } from '../../graphql/queries';
import { formatDate } from '../../lib/dateFormat';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './RoadmapTimelineWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'roadmap-timeline',
  title: 'Roadmap Timeline',
  size: 'large',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface RoadmapTimelineStats {
  roadmaps: RoadmapTimelineEntry[];
}

type RoadmapTimelineQueryData = ReturnType<typeof useRoadmapTimeline>['data'];

function extractRoadmapTimelineStats(data: RoadmapTimelineQueryData): RoadmapTimelineStats | null {
  if (!data?.roadmapTimeline) {
    return null;
  }
  return { roadmaps: data.roadmapTimeline };
}

/** Formats an ISO horizon date range for display; either end may be absent. */
function formatHorizon(horizonStart: string | null, horizonEnd: string | null): string {
  if (!horizonStart && !horizonEnd) return 'No horizon set';
  const start = horizonStart ? formatDate(horizonStart) : '?';
  const end = horizonEnd ? formatDate(horizonEnd) : '?';
  return `${start} – ${end}`;
}

export function RoadmapTimelineWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useRoadmapTimeline(),
    extractRoadmapTimelineStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.roadmaps.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="roadmap-timeline-widget__empty">
          <IconElement name="map" className="roadmap-timeline-widget__empty-icon" />
          <p className="forge-typography--body2">No roadmaps configured</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="roadmap-timeline-widget__list" data-testid="roadmap-timeline-list">
        {stats.roadmaps.map((roadmap) => (
          <li key={roadmap.id} className="roadmap-timeline-widget__roadmap">
            <div className="roadmap-timeline-widget__roadmap-header">
              <span className="roadmap-timeline-widget__roadmap-name forge-typography--body2">
                {roadmap.name}
              </span>
              {roadmap.scope && (
                <span className="roadmap-timeline-widget__scope forge-typography--caption">
                  {roadmap.scope}
                </span>
              )}
            </div>
            <div className="roadmap-timeline-widget__roadmap-horizon forge-typography--caption">
              {formatHorizon(roadmap.horizonStart, roadmap.horizonEnd)}
            </div>
            {roadmap.initiatives.length === 0 ? (
              <p className="roadmap-timeline-widget__no-initiatives forge-typography--caption">
                No initiatives on this roadmap
              </p>
            ) : (
              <ul
                className="roadmap-timeline-widget__initiatives"
                data-testid={`roadmap-initiatives-${roadmap.id}`}
              >
                {roadmap.initiatives.map((initiative) => (
                  <li key={initiative.id} className="roadmap-timeline-widget__initiative">
                    <span className="roadmap-timeline-widget__initiative-name">
                      {initiative.name}
                    </span>
                    {initiative.status && (
                      <span className="roadmap-timeline-widget__initiative-status">
                        {initiative.status}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
